import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getPartnerEarningExplanation } from '../../../api/partner/wallet.partner-api';
import { queryKeys } from '../../../api/queryKeys';
import { getEarningExplanation } from '../../../api/wallet.api';
import { isAdmin } from '../../../helpers/appType';
import { EarningExplanationResponse } from '../../../types/api/wallet.types';

/**
 * AI explanation of one order's payout. Fetched only while the explanation
 * modal is open - generation takes ~3-8s server side, so it must never fire
 * from a list render - and then held for the whole session.
 *
 * The four option overrides are load-bearing, not tuning (same trap as
 * useChatMessages documents): the client-wide defaults refetch on window focus
 * after 30s and retry 5xx once, which here would re-fire an 8s AI call on
 * alt-tab and take ~16s before an error surfaces. Errors surface immediately
 * and the modal's Try Again button is the only retry.
 *
 * Admin resolves the wallet owner from the route's `:id` - both admin routes
 * that mount IncomeDetailModal carry the userId there (/transactions/:id and
 * /wallet-pending/:id/:locationId). Deliberately ignored off admin: the
 * partner pending route also has an `:id`, but it is a LOCATION id there, and
 * the partner endpoint is token-scoped anyway.
 */
export const useEarningExplanation = ({
  orderId,
  enabled,
}: {
  orderId: number | null | undefined;
  enabled: boolean;
}) => {
  const { id: routeUserId } = useParams<{ id?: string }>();
  const userId = isAdmin ? routeUserId ?? null : null;

  const query = useQuery({
    queryKey: queryKeys.wallet.earningExplanation(userId, orderId ?? 'none'),
    queryFn: async (): Promise<EarningExplanationResponse> => {
      const { data } =
        isAdmin && userId != null
          ? await getEarningExplanation(userId, orderId as number)
          : await getPartnerEarningExplanation(orderId as number);
      return data.data;
    },
    enabled: enabled && orderId != null && (!isAdmin || userId != null),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  return {
    data: query.data,
    error: query.error,
    /**
     * True from the moment the query may run until data or an error lands,
     * and again during a Try Again refetch. Not `isLoading`: a disabled query
     * is never loading, and right after `enabled` flips true there is one
     * render where the query is pending but not yet fetching - branching on
     * isLoading would flash the loaded layout empty. An errored query is
     * stale, so closing and reopening the modal retries it for free, while
     * successful data (staleTime Infinity) never refetches.
     */
    isGenerating: query.isFetching || (!query.data && !query.error),
    refetch: query.refetch,
  };
};
